/* ==========================================================================
   tdc-theme.js — TradeDocsCo interaction layer for Shopify (Horizon)
   --------------------------------------------------------------------------
   Scroll reveals, stat count-ups, magnetic buttons, card tilt, the hero's
   interactive price-book demo, and the calculators.

   Three properties this file has to hold, because Shopify is not a static page:

     1. Everything is idempotent and re-runnable. The theme editor re-renders a
        section on every setting change and fires shopify:section:load, so a
        run-once top-level script would leave dead listeners and double-bound
        handlers. Each initialiser marks its root with a data flag and bails if
        already wired.
     2. Nothing is queried globally by id. Everything is scoped to its section
        root, so two calculators can share a page without colliding.
     3. Demo content comes from Liquid rather than a hardcoded object, so the
        hero's trades and rows stay editable in the theme editor.

   No dependencies. Safe to load with defer.
   ========================================================================== */

(function () {
  'use strict';

  /* Each section renders its own <script src> tag so the package needs no
     theme.liquid edit. The browser fetches the file once but EXECUTES it once
     per tag — this guard makes every run after the first a no-op, so listeners
     are never double-bound. */
  if (window.__tdcThemeLoaded) return;
  window.__tdcThemeLoaded = true;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var fine = window.matchMedia('(pointer:fine)').matches;

  document.documentElement.classList.add('tdc-js');

  /* ----------------------------------------------------------------------
     Reveal on scroll + stat count-up
     ---------------------------------------------------------------------- */

  var counted = new WeakSet();

  function countTo(el) {
    if (counted.has(el)) return;
    counted.add(el);
    var to = parseFloat(el.dataset.count);
    if (isNaN(to)) return;
    var comma = el.dataset.comma;
    var suf = el.dataset.suffix || '';
    if (reduce) {
      el.textContent = (comma ? to.toLocaleString() : to) + suf;
      return;
    }
    var dur = 1300;
    var start = performance.now();
    (function tick(now) {
      var p = Math.min(1, (now - start) / dur);
      var e = 1 - Math.pow(1 - p, 3);
      var v = Math.round(to * e);
      el.textContent = (comma ? v.toLocaleString() : v) + (p < 1 ? '' : suf);
      if (p < 1) requestAnimationFrame(tick);
      else el.textContent = (comma ? to.toLocaleString() : to) + suf;
    })(start);
  }

  var revealObserver = null;
  function getRevealObserver() {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;
    revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          e.target.classList.add('visible');
          var nums = e.target.querySelectorAll('[data-count]');
          for (var i = 0; i < nums.length; i++) countTo(nums[i]);
          revealObserver.unobserve(e.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -50px 0px' }
    );
    return revealObserver;
  }

  function initReveal(root) {
    var els = root.querySelectorAll('.tdc-reveal:not([data-tdc-revealed])');
    var io = reduce ? null : getRevealObserver();
    for (var i = 0; i < els.length; i++) {
      els[i].setAttribute('data-tdc-revealed', '');
      if (io) io.observe(els[i]);
      else {
        els[i].classList.add('visible');
        var nums = els[i].querySelectorAll('[data-count]');
        for (var j = 0; j < nums.length; j++) countTo(nums[j]);
      }
    }
  }

  /* ----------------------------------------------------------------------
     Hero entrance
     ---------------------------------------------------------------------- */

  function initHeroEntrance(root) {
    var anims = root.querySelectorAll('.tdc-anim:not([data-tdc-entered])');
    for (var i = 0; i < anims.length; i++) {
      anims[i].setAttribute('data-tdc-entered', '');
      anims[i].style.transitionDelay = i * 0.09 + 's';
      anims[i].classList.add('loaded');
    }
    var demo = root.querySelector('.tdc-demo');
    if (demo) demo.classList.add('loaded');
    var stats = root.querySelectorAll('[data-count]');
    setTimeout(function () {
      for (var k = 0; k < stats.length; k++) countTo(stats[k]);
    }, 700);
  }

  /* ----------------------------------------------------------------------
     Scroll progress bar
     ---------------------------------------------------------------------- */

  var progressBound = false;
  function initScrollProgress() {
    if (progressBound) return;
    var bar = document.querySelector('[data-tdc-progress]');
    if (!bar) return;
    progressBound = true;
    var onScroll = function () {
      var h = document.documentElement.scrollHeight - window.innerHeight;
      bar.style.width = (h > 0 ? (window.scrollY / h) * 100 : 0) + '%';
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  /* ----------------------------------------------------------------------
     Magnetic buttons + card tilt
     ---------------------------------------------------------------------- */

  function initPointerFlourish(root) {
    if (reduce || !fine) return;

    var mags = root.querySelectorAll('.tdc-magnetic:not([data-tdc-mag])');
    for (var i = 0; i < mags.length; i++) {
      (function (btn) {
        btn.setAttribute('data-tdc-mag', '');
        btn.addEventListener('mousemove', function (e) {
          var r = btn.getBoundingClientRect();
          var x = e.clientX - r.left - r.width / 2;
          var y = e.clientY - r.top - r.height / 2;
          btn.style.transform = 'translate(' + x * 0.18 + 'px, ' + y * 0.3 + 'px)';
        });
        btn.addEventListener('mouseleave', function () {
          btn.style.transform = '';
        });
      })(mags[i]);
    }

    var tilts = root.querySelectorAll('.tdc-tilt:not([data-tdc-tilt])');
    for (var j = 0; j < tilts.length; j++) {
      (function (card) {
        card.setAttribute('data-tdc-tilt', '');
        card.addEventListener('mousemove', function (e) {
          var r = card.getBoundingClientRect();
          var px = (e.clientX - r.left) / r.width - 0.5;
          var py = (e.clientY - r.top) / r.height - 0.5;
          card.style.transform =
            'translateY(-6px) rotateX(' + -py * 5 + 'deg) rotateY(' + px * 5 + 'deg)';
        });
        card.addEventListener('mouseleave', function () {
          card.style.transform = '';
        });
      })(tilts[j]);
    }
  }

  /* ----------------------------------------------------------------------
     Hero demo price book
     ---------------------------------------------------------------------- */

  function initDemo(root) {
    var demo = root.querySelector('[data-tdc-demo]');
    if (!demo || demo.hasAttribute('data-tdc-demo-ready')) return;

    var dataEl = demo.querySelector('[data-tdc-demo-data]');
    if (!dataEl) return;

    var trades;
    try {
      trades = JSON.parse(dataEl.textContent);
    } catch (err) {
      return;
    }
    if (!trades || !trades.length) return;

    demo.setAttribute('data-tdc-demo-ready', '');

    var chipWrap = demo.querySelector('[data-tdc-demo-chips]');
    var table = demo.querySelector('[data-tdc-demo-table]');
    var fileEl = demo.querySelector('[data-tdc-demo-file]');
    var slider = demo.querySelector('[data-tdc-demo-slider]');
    var rateEl = demo.querySelector('[data-tdc-demo-rate]');
    var linkEl = demo.querySelector('[data-tdc-demo-link]');
    var linkLabel = demo.querySelector('[data-tdc-demo-link-label]');
    var serviceCountEl = demo.querySelector('[data-tdc-demo-service-count]');
    if (!chipWrap || !table || !slider) return;

    var current = 0;
    var money = function (n) {
      return '$' + Math.round(n).toLocaleString();
    };

    function syncTrade() {
      var t = trades[current];
      var chips = chipWrap.querySelectorAll('.tdc-demo-chip');
      for (var k = 0; k < chips.length; k++) {
        var active = k === current;
        chips[k].classList.toggle('active', active);
        chips[k].setAttribute('aria-pressed', active ? 'true' : 'false');
      }
      if (fileEl && t.file) fileEl.textContent = t.file;
      if (serviceCountEl) serviceCountEl.textContent = t.serviceCount || '';
      if (linkEl && t.url) linkEl.href = t.url;
      if (linkLabel) linkLabel.textContent = t.cta || 'Explore the ' + t.name + ' Price Book';
    }

    function buildChips() {
      chipWrap.innerHTML = '';
      trades.forEach(function (t, i) {
        var c = document.createElement('button');
        c.type = 'button';
        c.className = 'tdc-demo-chip' + (i === current ? ' active' : '');
        c.setAttribute('aria-pressed', i === current ? 'true' : 'false');
        c.textContent = t.name;
        c.addEventListener('click', function () {
          current = i;
          syncTrade();
          buildRows();
        });
        chipWrap.appendChild(c);
      });
    }

    function buildRows() {
      table.innerHTML = '';
      (trades[current].rows || []).forEach(function (r) {
        var row = document.createElement('div');
        row.className = 'tdc-demo-row' + (r.hot ? ' hot' : '');

        var svc = document.createElement('span');
        svc.className = 'svc';
        svc.appendChild(document.createTextNode(r.svc || ''));
        if (r.note) {
          var small = document.createElement('small');
          var note = document.createElement('span');
          note.textContent = r.note;
          small.appendChild(note);
          svc.appendChild(small);
        }

        var amt = document.createElement('span');
        amt.className = 'amt tdc-amt';
        amt.textContent = '$0';

        row.appendChild(svc);
        row.appendChild(amt);
        table.appendChild(row);
      });
      priceRows(false);
    }

    function priceRows(flash) {
      var rate = parseFloat(slider.value) || 0;
      var amts = table.querySelectorAll('.amt');
      (trades[current].rows || []).forEach(function (r, i) {
        if (!amts[i]) return;
        var val = (parseFloat(r.h) || 0) * rate + (parseFloat(r.mat) || 0);
        amts[i].textContent = money(val);
        if (flash && !reduce) {
          amts[i].classList.remove('flash');
          void amts[i].offsetWidth;
          amts[i].classList.add('flash');
        }
      });
    }

    function updateFill() {
      var min = parseFloat(slider.min) || 0;
      var max = parseFloat(slider.max) || 100;
      var pct = max === min ? 0 : ((slider.value - min) / (max - min)) * 100;
      slider.style.background =
        'linear-gradient(90deg, var(--tdc-gold) 0%, var(--tdc-gold) ' +
        pct +
        '%, var(--tdc-gray-200) ' +
        pct +
        '%)';
    }

    /* A bare range input announces "125" with no unit. aria-valuetext gives
       screen readers "$125 per hour" instead (WCAG 4.1.2). */
    function updateValueText() {
      var suffix = slider.getAttribute('data-tdc-valuetext-suffix') || '';
      slider.setAttribute('aria-valuetext', '$' + slider.value + suffix);
    }

    slider.addEventListener('input', function () {
      if (rateEl) rateEl.textContent = slider.value;
      updateValueText();
      updateFill();
      /* No flash while dragging. The flash restarts on every input event, so on
         a continuous drag every amount stays pinned at the gold keyframe and the
         whole column smears gold until release. Numbers still update live here;
         only the featured (.hot) row stays gold, which is the intent. */
      priceRows(false);
    });
    /* One confirming gold pulse when the drag is committed, not during it. */
    slider.addEventListener('change', function () {
      priceRows(true);
    });

    if (fileEl && trades[0].file) fileEl.textContent = trades[0].file;
    if (rateEl) rateEl.textContent = slider.value;
    buildChips();
    syncTrade();
    buildRows();
    updateFill();
    updateValueText();
  }

  /* ----------------------------------------------------------------------
     Calculators
     ---------------------------------------------------------------------- */

  function num(root, name) {
    var el = root.querySelector('[data-field="' + name + '"]');
    if (!el) return 0;
    var v = parseFloat(el.value);
    return isNaN(v) ? 0 : v;
  }

  function put(root, name, text) {
    var el = root.querySelector('[data-out="' + name + '"]');
    if (el) el.textContent = text;
  }

  var money2 = function (v) {
    return (
      '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    );
  };
  var money0 = function (v) {
    return '$' + v.toLocaleString('en-US', { maximumFractionDigits: 0 });
  };
  var pct1 = function (v) {
    return v.toFixed(1) + '%';
  };
  var whole = function (v) {
    return v.toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  /* Markup vs margin — HOMEPAGE TEASER only.
     The teaser clamps rather than blanking, and rounds to whole dollars and
     whole percent. The full calculator page does neither — see calcMarginFind /
     calcMarginCheck below. Keeping them separate is deliberate: collapsing the
     two changes the numbers the teaser shows. */
  function calcMargin(root) {
    var cost = Math.max(0, num(root, 'cost'));
    var m = Math.min(99, Math.max(0, num(root, 'margin')));
    var price = m < 99 ? cost / (1 - m / 100) : cost;
    var markup = cost > 0 ? ((price - cost) / cost) * 100 : 0;
    put(root, 'price', money0(price));
    put(root, 'markup', Math.round(markup) + '%');
    put(root, 'profit', money0(price - cost));
  }

  /* Markup calculator page — panel A, "Find My Price".
     Blanks to em-dash unless cost is truthy and 0 < margin < 100. */
  function calcMarginFind(root) {
    var cost = num(root, 'cost');
    var margin = num(root, 'margin');
    if (!cost || !margin || margin <= 0 || margin >= 100) {
      put(root, 'price', '—');
      put(root, 'profit', '—');
      put(root, 'markup', '—');
      return;
    }
    var price = cost / (1 - margin / 100);
    var profit = price - cost;
    put(root, 'price', money2(price));
    put(root, 'profit', money2(profit));
    put(root, 'markup', pct1((profit / cost) * 100));
  }

  /* Markup calculator page — panel B, "Check My Numbers".
     Blanks unless both values are truthy and cost < price. */
  function calcMarginCheck(root) {
    var price = num(root, 'price-in');
    var cost = num(root, 'cost-in');
    if (!price || !cost || cost >= price) {
      put(root, 'margin', '—');
      put(root, 'profit', '—');
      put(root, 'markup', '—');
      return;
    }
    var profit = price - cost;
    put(root, 'margin', pct1((profit / price) * 100));
    put(root, 'profit', money2(profit));
    put(root, 'markup', pct1((profit / cost) * 100));
  }

  /* Job cost estimator */
  function calcJobCost(root) {
    var direct =
      Math.max(0, num(root, 'labor-hours')) * Math.max(0, num(root, 'labor-rate')) +
      Math.max(0, num(root, 'materials')) +
      Math.max(0, num(root, 'subs'));
    var overheadDollars = direct * (Math.max(0, num(root, 'overhead')) / 100);
    var total = direct + overheadDollars;
    var marginPct = num(root, 'margin');
    var sell = total;
    var profit = 0;
    if (marginPct > 0 && marginPct < 100) {
      sell = total / (1 - marginPct / 100);
      profit = sell - total;
    }
    var markup = total === 0 ? 0 : (profit / total) * 100;
    var blank = direct === 0;

    put(root, 'sell-price', blank ? '—' : money2(sell));
    put(root, 'job-cost', blank ? '—' : money2(total));
    put(root, 'profit', blank ? '—' : money2(profit));
    put(root, 'direct-cost', blank ? '—' : money2(direct));
    put(root, 'overhead-dollars', blank ? '—' : money2(overheadDollars));
    put(root, 'markup', blank ? '—' : pct1(markup));
  }

  /* Overhead rate */
  function calcOverhead(root) {
    var annualOverhead = Math.max(0, num(root, 'monthly-overhead')) * 12;
    var annualHours =
      Math.max(0, num(root, 'tech-count')) *
      Math.max(0, num(root, 'billable-hours')) *
      Math.max(0, num(root, 'weeks-year'));
    var overheadPerHour = annualHours === 0 ? 0 : annualOverhead / annualHours;
    var breakEven = Math.max(0, num(root, 'loaded-wage')) + overheadPerHour;
    var targetProfit = num(root, 'target-profit');
    var target =
      targetProfit > 0 && targetProfit < 100 ? breakEven / (1 - targetProfit / 100) : breakEven;
    var blank = annualHours === 0 && annualOverhead === 0;

    put(root, 'target-rate', blank ? '—' : money2(target));
    put(root, 'break-even-rate', blank ? '—' : money2(breakEven));
    put(root, 'overhead-hour', blank ? '—' : money2(overheadPerHour));
    put(root, 'annual-overhead', blank ? '—' : money2(annualOverhead));
    put(root, 'annual-hours', blank ? '—' : whole(annualHours));
    put(root, 'labor-burden', blank ? '—' : money2(breakEven));
  }

  var CALCS = {
    margin: calcMargin,
    'margin-find': calcMarginFind,
    'margin-check': calcMarginCheck,
    'job-cost': calcJobCost,
    overhead: calcOverhead
  };

  /* Tabbed calculators ("Find My Price" / "Check My Numbers") */
  function initTabs(root) {
    var groups = root.querySelectorAll('[data-tdc-tabs]:not([data-tdc-tabs-ready])');
    for (var i = 0; i < groups.length; i++) {
      (function (group) {
        group.setAttribute('data-tdc-tabs-ready', '');
        var btns = group.querySelectorAll('[data-tdc-tab]');
        var panels = group.querySelectorAll('[data-tdc-panel]');

        function select(name) {
          for (var a = 0; a < btns.length; a++) {
            var on = btns[a].getAttribute('data-tdc-tab') === name;
            btns[a].classList.toggle('active', on);
            btns[a].setAttribute('aria-selected', on ? 'true' : 'false');
            btns[a].setAttribute('tabindex', on ? '0' : '-1');
          }
          for (var b = 0; b < panels.length; b++) {
            var show = panels[b].getAttribute('data-tdc-panel') === name;
            panels[b].classList.toggle('active', show);
            panels[b].hidden = !show;
          }
        }

        for (var k = 0; k < btns.length; k++) {
          (function (btn) {
            btn.addEventListener('click', function () {
              select(btn.getAttribute('data-tdc-tab'));
            });
            btn.addEventListener('keydown', function (e) {
              if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
              e.preventDefault();
              var list = Array.prototype.slice.call(btns);
              var at = list.indexOf(btn);
              var next = list[(at + (e.key === 'ArrowRight' ? 1 : list.length - 1)) % list.length];
              select(next.getAttribute('data-tdc-tab'));
              next.focus();
            });
          })(btns[k]);
        }

        if (btns.length) select(btns[0].getAttribute('data-tdc-tab'));
      })(groups[i]);
    }
  }

  function initCalculators(root) {
    var calcs = root.querySelectorAll('[data-tdc-calc]:not([data-tdc-calc-ready])');
    for (var i = 0; i < calcs.length; i++) {
      (function (el) {
        var fn = CALCS[el.getAttribute('data-tdc-calc')];
        if (!fn) return;
        el.setAttribute('data-tdc-calc-ready', '');
        var inputs = el.querySelectorAll('input');
        for (var k = 0; k < inputs.length; k++) {
          inputs[k].addEventListener('input', function () {
            fn(el);
          });
        }
        fn(el);
      })(calcs[i]);
    }
  }

  /* ----------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */

  function initAll(root) {
    var scope = root || document;
    initReveal(scope);
    initPointerFlourish(scope);
    initDemo(scope);
    initTabs(scope);
    initCalculators(scope);
    initScrollProgress();
    // Both the homepage hero and the trade-collection hero use .tdc-anim, which
    // is hidden (opacity:0) until the entrance runs. Init every hero in scope —
    // querying only '.tdc-hero' left the collection hero permanently invisible.
    var heroes = scope.querySelectorAll('.tdc-hero, .tdc-collection-hero');
    for (var hi = 0; hi < heroes.length; hi++) initHeroEntrance(heroes[hi]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  /* Theme editor: a section re-renders on every setting change */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();